import { Component, OnInit, AfterContentInit, OnDestroy, Input, Output, EventEmitter,
		NgZone, OnChanges, SimpleChange, SimpleChanges } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { MenuItem } from 'primeng/api';
import { ChartUtilitiesService } from './chart-utilities.service';
import { DataLayerService } from './data-layer.service';
import { DashboardTemplateService } from './dashboard-template.service';
import { StationDetailsService } from './station-details.service';
import { ConfigService } from './config.service';
import { AppConfigService } from './app-config.service';

import * as Highcharts from 'highcharts';
import Annotations from 'highcharts/modules/annotations';
import BrokenAxis from 'highcharts/modules/broken-axis';
import HC_exporting from 'highcharts/modules/exporting';
import HC_exportdata from 'highcharts/modules/export-data';
Annotations(Highcharts);
BrokenAxis(Highcharts);
HC_exporting(Highcharts);
HC_exportdata(Highcharts);

// this allows access to jquery references such as $("body")
declare var $;
declare var require: any;


// Because typscript must have definitions for all functions and highlight is a custom fuction:
declare module 'highcharts' {
	// let doc: any;
	// let win: any;
	interface Point {
		highlight: (event: Highcharts.PointerEventObject) => void;
	}
	interface Chart {
		addButton: (options: any) => void;
		contextMenu: (className, items, x, y, width, height, button, event) => void;
	}
}

declare var L;

//
// This component allows users to create / edit templates.
//

@Component({
	selector: 'dashboard-template-component',
	templateUrl: 'dashboard-template.component.html',
	styleUrls: ['dashboard-template.component.css'],
	providers: [StationDetailsService]
})
export class DashboardTemplateComponent implements OnInit, AfterContentInit, OnChanges, OnDestroy {

	@Input() isHeaderVisible = true;
	@Input() preview = false;
	@Input() previewTemplate: any;
	@Input() previewTemplateCounter: any;
	@Output() getStationList = new EventEmitter<any[]>();

	dataLayerService: DataLayerService;
	dashboardTemplateService: DashboardTemplateService;
	stationID = '-999';
	listSelection;
	// stationList is a subset of originalStationList which contains only the stations resulting from filtering, if specified, for the current custom dashboard
	stationList = [];
	// originalStationList saves the original list of stations after sorting
	originalStationList = [];
	stationNovaScores = [];
	currentStation = { stationName: '', stationId: '', stationElevation: '', latitude: '40', longitude: '-105' };
	tzName = '';
	mapVisible = false;
	imagesVisible = false;
	headerVisible = this.isHeaderVisible;
	templateID = '';
	station_name = '';  // variable from the json file associated with the name of the station
	filterDefault = [];

	images: any[] = [];
	allImages = [];
	displayXAxisOnEveryGraph = false;
	startDate;
	endDate;

	dataAccessOptions: MenuItem[];

	periodOfRecordList = [
		{ label: '1 week', value: '7' },
		{ label: '1 month', value: '30' },
		{ label: '3 months', value: '90' },
		{ label: '6 months', value: '180' },
		{ label: '1 year', value: '365' },
		{ label: '10 years', value: '3650' },
		{ label: 'custom', value: '0'}];

	periodOfRecordSelected = '7';

	// This is the list of colors used when more than 1 time series is available for a given data type
	colorList = ['green', 'orange', 'deeppink', 'indigo', 'maroon'];

	// stationTSArray defines the graph content as well as issues concerning novascore, including
	// the pulldown station list,
	stationTSArray: any[] = [];

	stationMap: any = null;
	stationMarker: any = null;

	constructor(dataLayerService: DataLayerService,
		dashboardTemplateService: DashboardTemplateService,
		private chartUtilitiesService: ChartUtilitiesService,
		private stationDetailsService: StationDetailsService,
		private configService: ConfigService,
		private appConfigService: AppConfigService,
		private location: Location,
		private _router: Router,
		private route: ActivatedRoute ) 
	{
			console.log('In dashboard-template constructor.');
			this.dataLayerService = dataLayerService;
			this.dashboardTemplateService = dashboardTemplateService;
			this.displayXAxisOnEveryGraph = this.appConfigService.getDashboardPreference('displayXAxisOnEveryGraph', this.displayXAxisOnEveryGraph );
			this.setStartAndEndDateDefaults();
			// console.log( 'Setting allImages in dashboard-template constructor', this.allImages );
	}

	ngOnInit() {
		console.log('in ngOnInit');

		this.dataAccessOptions = [
			{ label: '', icon: 'pi pi-fw pi-chart-bar',
				items: [
					{ label: 'Download CSV', icon: 'pi pi-download', 
						command: (event) => { this.stationDetailsService.downloadData('csv', this.stationID, this.stationTSArray, this.periodOfRecordSelected, this.startDate, this.endDate ); }},
					{ label: 'Download JSON', icon: 'pi pi-download', 
						command: (event) => { this.stationDetailsService.downloadData('json', this.stationID, this.stationTSArray, this.periodOfRecordSelected, this.startDate, this.endDate ); }}
				]
			}
		];

		// Setup map component.  Then set to not visible until requested.  This will only allow for 1 map.
		// Perhaps will need to change to actually creating a map only when the user requests in case
		// more than 1 map is desired.  Not sure why that would be the case...
		// Cat - see https://www.drupal.org/project/leaflet_markercluster/issues/2929165#comment-12962104
		let map_container = L.DomUtil.get('mapid');
		if ( map_container != null ) {
			console.log('Clearing leaflet id.  Why?');
			map_container._leaflet_id = null;
		}

		this.stationMap = new L.map('mapid');
		this.stationMap.setView([51, 0], 13);
		L.tileLayer('http://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}',
			{ subdomains: ['mt0', 'mt1', 'mt2', 'mt3'],
			attribution: '<a href="https://maps.google.com">Map data: Google</a>'}).addTo(this.stationMap);

		// this allows the x-axis and tooltip to display the date in the local browser time zone
		Highcharts.setOptions({
			time: {
				useUTC: false
			},
			lang: {
				thousandsSep: ','
			}
		});

		// set the local time zone string
		const d = new Date();
		this.tzName = d.toLocaleString('en', {timeZoneName: 'short'}).split(' ').pop();
		console.log(this.tzName);

		// fill the pulldown with the id and name of the available stations
		this.populateStationSelection();

		this.dashboardTemplateService.configurationCompleteObserver.subscribe( isReady => {
			console.log ( 'Received value from configurationCompleteOberver:' + isReady );
			if ( isReady == true ) {

			this.allImages = this.dashboardTemplateService.getAllImages();

			if ( this.preview === false ) {
				// this is like a listener so dashboard TemplateSelected gets called if a different template is selected.
				// for some reason, params leaves off the ?id=<template name>
				this.route.params.forEach((params: Params) => {
					console.log(params);
					// this.dashboardTemplateSelected(params['id']);
					const path = this.location.path();
					const parts = path.split('/dashboardTemplate/');
					const decodedURI = decodeURIComponent(parts[1]);
					this.dashboardTemplateSelected(decodedURI);
				});
			}

			this.displayGraphsForStationID();
			}
		});

		console.log('Calling bindMouseEvents');
		this.chartUtilitiesService.bindMouseEvents($('#chartContainer'));
		this.chartUtilitiesService.fixExportMenu();
	}

	ngAfterContentInit() {
		// this.stationMap.redraw();
	}

	ngOnChanges( changes: SimpleChanges ) {
		console.log("In ngOnChanges!!!!");
		// console.log( changes );
		this.setDataElements ( this.previewTemplate );
		this.setDefaultStation();
	}

	ngOnDestroy() {
		console.log ("In ngOnDestroy");
		/*
		** if auto refresh is enabled, unsubscribe from the subscription here
		*/
	}

	onGetStationList() {
		this.getStationList.emit(this.stationList);
	}

	setHeaderVisible ( bool: boolean ) {
		this.isHeaderVisible = bool;
	}

	// set end date to now
	// set start date to 1 week ago
	setStartAndEndDateDefaults() {
		this.endDate = new Date(Date.now());
		this.startDate = new Date (this.endDate - (7 * 24 * 60 * 60 * 1000));
	}

	// Filter originalStationList using filterDefault, assigning the results to stationList
	filterStationList( ) {
		if ( this.filterDefault.length == 0 ){
			console.log ('Including all stations since no filter has been specified.');
			this.stationList = this.originalStationList;
		} else {
			this.stationList = [];
			for ( let i = 0; i < this.originalStationList.length; i++) {
				// first test if this station should be included based on custom dashboard configuration
					const qualifyingFilter = this.filterDefault.filter ( filterString => this.originalStationList[i][this.station_name].includes(filterString));
					if ( qualifyingFilter.length > 0 ) {
						// include this station
						this.stationList.push ( this.originalStationList[i]);
					}
			}
			// console.log ( this.stationList );
		}
	}

	// each time a new dashboard template is selected, this routine extracts
	// the station id and the template name
	dashboardTemplateSelected ( idString: string ) {
		console.log(idString);
		const urlParts = idString.split('?');
		console.log(urlParts);
		if ( urlParts.length > 0 ) {
			this.stationID = urlParts[0];  // this stationID is a string
		}
		if ( urlParts.length > 1 ) {
			const queryString = urlParts[1].split('&');
			console.log(queryString);
			queryString.forEach((queryStringPart) => {
				const queryStringParts = queryStringPart.split('=');
				console.log(queryStringParts);
				if ( queryStringParts.length > 1 && queryStringParts[0] === 'id' ) {
					this.processDashboardTemplate(queryStringParts[1]);
				}
			}, this);
		}
	}

	//
	// Using the templateID, find the associated dashboard template and set the
	// elements in the dashboard by calling setData
	// CAT - here refilter the station list
	processDashboardTemplate (templateID: string) {
		console.log(templateID);
		this.templateID = templateID;
		// console.log(this.dashboardTemplateService.getDashboardTemplates());
		// console.log(this.dashboardTemplateService.getDashboardTemplate(templateID));

		// set map to not visible, then visible later if requested in the template
		this.mapVisible = false;
		this.imagesVisible = false;
		this.filterDefault = [];

		// set the data for this template
		this.setData(this.dashboardTemplateService.getDashboardTemplate(templateID));
		this.filterStationList( );
		this.setDefaultStation();
	}

	//
	// Process the dashboardTemplate elements: create the map, charts, etc.
	//  example of what the chart template element might look like:
	// 	{
	// 		dataType: "Precip-Total",	// used in graph
	// 		color: "cyan",			// used in graph
	// 		chartType: 'column', 		// used in graph
	// 		statisticType: "Total",		// used for novascore
	// 		duration: "1Hour"		// used for novascore
	// 	}
	//
	// dashboardTemplate contains: { label: "Water Quality", id" "WaterQual", elements: {like above} }
	//
	//
	setData ( dashboardTemplate: any ) {
		console.log('Setting data: ');
		console.log(dashboardTemplate);
		if ( dashboardTemplate.hasOwnProperty('filterDefault')) {
			this.filterDefault = dashboardTemplate.filterDefault;
		}
		if ( dashboardTemplate.hasOwnProperty('elements')) {
			const elements = dashboardTemplate.elements;
			this.setDataElements ( elements );
		}
	}

	// process each element in the configuration - 
	//  if the type is chart, add to the stationTSArray
	//  if the type is map, display the map
	//  if the type is images, display the images
	setDataElements ( elements ) {
		// console.log(elements);
		this.mapVisible = false;
		this.imagesVisible = false;
		this.stationTSArray = [];
		elements.forEach( function(item) {
			if ( item.hasOwnProperty('type')) {
				if ( item.type === 'chart') {
					this.stationTSArray.push ( item );
				} else if ( item.type === 'map') {
					this.mapVisible = true;
					this.displayMap();
				} else if ( item.type === 'images') {
					this.imagesVisible = true;
					this.displayImages();
				}
			}
		}, this );
		// console.log( this.mapVisible, this.imagesVisible );
	}

	// process the entire list of images and display those whose id matches the current station id
	displayImages() {
		// console.log('In displayImages', this.imagesVisible, this.allImages, this.currentStation.stationId );
		this.images = [];
		if ( this.imagesVisible === false ||
				this.allImages.length == 0 ||
				this.currentStation.stationId === '') {
			console.log('Something amiss in displayImages');
			return;
		}
		const id = '' + this.currentStation.stationId;
		// console.log(this.currentStation);

		const imagesDefinition = this.allImages.filter(function(item) {
			// console.log('Comparing', item.stationId, id);
			return item.stationId === id;
		})[0];
		if ( imagesDefinition === undefined || imagesDefinition.length === 0 ) {
			return;
		}
		// console.log ( imagesDefinition );
		imagesDefinition.images.forEach(element => {
			this.images.push({ source: this.configService.baseDir + element.source,
				thumbnail: this.configService.baseDir + element.thumbnail, title: element.title });
		}, this);
	}

	//
	// displayMap is called once map has been specified by the current template.
	// Currently, either the map is displayed or not.  No additional options are available
	// such as type of background tile, marker, etc.
	//
	displayMap() {
		console.log ('In displayMap', this.mapVisible);
		if ( this.mapVisible === false ) {
			return;
		}

		if ( this.stationMarker != null ) {
			console.log ('Removing marker');
			// console.log(this.stationMarker);
			this.stationMap.removeLayer(this.stationMarker);
		}

		const markerIcon = L.ExtraMarkers.icon({
				icon: 'fa-number',
				markerColor: 'yellow',			// marker background color
				shape: 'square',
				prefix: 'fa',
				number: ''
			});
		markerIcon.options.shadowSize = [50, 75];
		this.stationMarker = L.marker([this.currentStation.latitude, this.currentStation.longitude],
			{icon: markerIcon}).addTo(this.stationMap);
		this.stationMap.setView([this.currentStation.latitude, this.currentStation.longitude], 13);
	}


	//
	// Displays graphs for a given station id.
	//
	async displayGraphsForStationID () {
		// -999 is used when there is no stationID yet - no graphs to display.
		// May need to change this now that station id could be specified in stationTSArray.
		if ( this.stationID === '-999' ) {
			console.log('No station id specified yet');
			return;
		} else {
			console.log('Retrieving data for ' + this.stationID);
		}

		// Loop through each chart
		let lastGraphFound = false;
		// console.log(this.stationTSArray);
		for ( let i = this.stationTSArray.length - 1; i >= 0; i-- ) {

			// Loop through each time series per each chart
			for ( let j = 0; j < this.stationTSArray[i].data.length; j++ ) {
				const currentTSElement = this.stationTSArray[i].data[j];
				// console.log(currentTSElement);

				// If this element has a station_id specified, use that.  Otherwise use the global station_id.
				let stationID = this.stationID;
				if ( currentTSElement.hasOwnProperty('station_id') && currentTSElement.station_id != null ) {
					stationID = currentTSElement.station_id;
					console.log('This dashboard has a set station id (' + stationID +
						').  Not responding to station selections.');
				}
				// console.log(stationID);

				const filename = 'stationSummaries_' + stationID + '.json';
				console.log(filename);

				// first get all the ts data
				console.log('Retrieving data for ' + filename );
				const requestObject = {};
				if ( this.periodOfRecordSelected != '0' ) {
					// a duration has been selected
					requestObject['filename'] = filename; 

					// for now, we have to specify a future date to data web services to get future data
					// requestObject['duration'] = this.periodOfRecordSelected;
					const today = new Date(Date.now());
					const startDate = new Date(today.getTime() - (+this.periodOfRecordSelected * 24 * 60 * 60 * 1000 ));
					const endDate = new Date(today.getTime() + (24 * 60 * 60 * 1000 )); // 24 hours from now
					requestObject['periodStart'] = startDate;
					requestObject['periodEnd'] = endDate;
				} else {
					// a custom set of dates have been specified by the user
					requestObject['filename'] = filename;
					requestObject['periodStart'] = this.startDate;
					requestObject['periodEnd'] = this.endDate;
				}

				const tsfile = await this.stationDetailsService.getStationDetails(requestObject).toPromise();

				// tsfile.stationSummaries[0] always works because there is only 1 station in each ts file
				if ( tsfile.stationSummaries.length === 0 ) {
					console.log('No data returned for station ' + stationID);
					currentTSElement.ts = [];
				} else {

					currentTSElement.ts = tsfile.stationSummaries[0].ts.filter(currentTS => currentTS.dataType === currentTSElement.dataType);// [0];
					currentTSElement.station_id_label = stationID;
					this.stationTSArray[i].showXAxisLabels = false;

					// alarm triggers
					// this is cheesy, only for stage and only for type=upper - improve when precip alarms gets added by making more abstract

					const dataType = currentTSElement.dataType;
					if ( dataType === 'WaterLevelRiver' ) {
						let alarmTrigger = null;
						const dataTypesInfo = tsfile.stationSummaries[0].dataTypes.filter(currentTS => currentTS.name === dataType)[0];
						if ( dataTypesInfo != null ) {
							const alarmTriggerIds = dataTypesInfo.alarmTriggerIds;
							// console.log(alarmTriggerIds);
							alarmTriggerIds.forEach(function(element) {
								const tmpAlarmTrigger = tsfile.alarmTriggers.filter(currentAlarm => currentAlarm.id === element)[0];
								if ( tmpAlarmTrigger.type === 'upper' ) {
									alarmTrigger = tmpAlarmTrigger;
								}
							});
						}
						currentTSElement['alarmTrigger'] = alarmTrigger;

						if ( alarmTrigger != null ) {
							let alarmTitle = alarmTrigger.name;
							console.log(alarmTitle);
							if ( alarmTitle === '' || alarmTitle == null ) {
								alarmTitle = alarmTrigger.description;
							}
							currentTSElement['alarmTriggerTitle'] = alarmTitle;
						}
					}

					// figure out which graph is the last one to have data.  This is the one that will show the x-axis
					// CAT - do i need to chck for ts.length > 0?
					if ( this.stationTSArray[i].data[j].ts != null && !lastGraphFound ) {
						this.stationTSArray[i].showXAxisLabels = true;
						lastGraphFound = true;
						console.log('Last graph is ' + i);
					}
				}
			}
		}

		// data has been gathered, now build the graphs
		this.displayStationTimeSeries ( );
	}

	//
	// displayStationTimeSeries
	//
	displayStationTimeSeries ( ) {
		// console.log(tsfile);
		console.log('in displayStationTimeSeries');
		if ( this.stationTSArray == null ) {
			console.log('stationTSArray is null - no info available.');
			return;
		}

		const self = this;
		const minDate = this.chartUtilitiesService.getMinDate(this.stationTSArray);
		let maxDate = this.chartUtilitiesService.getMaxDate(this.stationTSArray);
		// console.log('Chart min and max dates', minDate, maxDate);
		// maxDate = maxDate + (7*24*60*60*1000);  // test it
		// console.log('Chart min and max dates', minDate, maxDate);

		// get the time series
		for ( let i = 0; i < this.stationTSArray.length; i++ ) {
			const currentChart = this.stationTSArray[i];
			// console.log(currentChart);
			const numberOfDays = this.numberOfDaysInChart ();

			const seriesArray: any[] = [];
			const annotationArray = [];
			const showXAxisLabels = currentChart.showXAxisLabels || this.displayXAxisOnEveryGraph;
			const lineColor = ( showXAxisLabels ) ? '#ccd6eb' : 'transparent';
			const tickLength = ( showXAxisLabels ) ? 10 : 0;
			const xAxisFormat = ( numberOfDays > 365 ) ? '{value:%Y %b}' : 
				numberOfDays === 365 ? '{value:%b}' :
				'{value:%b %d}';

			// Set tick interval for 1 year (10 year POR), 1 month (3 month to 1 year POR) and 1 day (all others) tick interval
			const tickInterval = numberOfDays > 365 ? 12 * 30 * 24 * 3600 * 1000 :  // 1 year
					numberOfDays > 30 ? 30 * 24 * 3600 * 1000 : // 1 month
					24 * 3600 * 1000; // 1 day
			console.log( 'Tick interval: ' + tickInterval );

			let isPrecip = true;
			let yAxisLabel = '';
			let yAxis = [];
			let units;
			let precision;
			let gapSize;

			let currentChartsWithData = 0;
			for ( let j = 0; j < currentChart.data.length; j++ ) {
				// let yAxisLabel = '';
				let seriesLabel = '';  // this is the small legend in the upper right corner of the chart
				// let isPrecip = true;
				// let units;
				const currentTSElement = currentChart.data[j];
				// console.log(currentTSElement);

				
				const tsArray = currentTSElement.ts;
				if ( tsArray != null && tsArray.length > 0 ) {
					for ( let k = 0; k < tsArray.length; k++ ) {
					const ts = tsArray[k];
					const color = tsArray.length === 1 ? currentTSElement.color : this.colorList[k % this.colorList.length];
					// console.log("setting line color to " + color);
					const chartType = currentTSElement.chartType;
					units = ts.units;
				
					// precision of y-axis labels and tooltip
					// set to value in configuration file
					// if that doesn't exist, set to recommendation from DWS
					// otherwise, set tooltip precision to 2, yaxis label to 1
					const valueDigits = ts.hasOwnProperty('valueDigits') ? ts.valueDigits : null;
					const dataType = currentTSElement.dataType;  // set dataType to element dataType
					const tooltipPrecision = currentTSElement.hasOwnProperty('tooltipPrecision') ? currentTSElement.tooltipPrecision : 
						valueDigits != null ? valueDigits + 1 : 2;
					precision = currentTSElement.hasOwnProperty('yAxisLabelPrecision') ? 
						currentTSElement.yAxisLabelPrecision :
						valueDigits != null ? valueDigits : 1;
					gapSize = ts.hasOwnProperty('properties') && ts.properties.hasOwnProperty('pointNoReportInterval') ? ts.properties.pointNoReportInterval*1000 : 86400000;  // # milliseconds in a day

					// create the yAxisLabel, which is also the legend
					// use the label, if specified, from the configuration table, otherwise
					// show the data type and then data interval (unless it includes the word irreg)
					const seriesIdentifierString = currentTSElement.hasOwnProperty('seriesIdentifier') ? currentTSElement.seriesIdentifier : null;
					const seriesIdentifier = ( seriesIdentifierString != null &&  ts.hasOwnProperty(seriesIdentifierString) && ts[seriesIdentifierString] != "") ? 
							ts[seriesIdentifierString] : '';
				    // console.log(currentTSElement, seriesIdentifierString, seriesIdentifier );
					const partialLabel = dataType + (( ts.dataInterval.toUpperCase().includes('IRREG')) ? ''  : ('.' + ts.dataInterval)) + ' (' + units + ')';
					yAxisLabel = currentTSElement.hasOwnProperty('yAxisLabel') ? currentTSElement.yAxisLabel.replace('${units}', units) : partialLabel;
					seriesLabel = 
							( seriesIdentifier != '' ? seriesIdentifier : dataType ) +
							(( ts.dataInterval.toUpperCase().includes('IRREG')) ? ''  : ('.' + ts.dataInterval)) +
							' (' + currentTSElement.station_id_label + ')';

					isPrecip = dataType.toUpperCase().includes('PRECIP') ? true : false;  // precip should be displayed from the top of the graph

					// line/bar vs markers only
					let lineWidth = 2;
					let markerEnabled = false;
					let markerSymbol = currentTSElement.hasOwnProperty('symbol') ? currentTSElement.symbol : 'triangle';
					if ( chartType.toUpperCase()==='SCATTER' || ( currentTSElement.hasOwnProperty('markerOnly') && currentTSElement.markerOnly === true )) {
						console.log('marker only');
						lineWidth = 0;
						markerEnabled = true;
						gapSize = 0;  // disregard gapSize - show everything if marker only
					}

					const standardYAxis = {
							title: {
								text: yAxisLabel
							},
							reversed: isPrecip,
							labels: {
								formatter: function () {
									// this allows the y axis labels to be replace with strings rather than their numeric value
									if ( currentTSElement.hasOwnProperty('yAxisReplace')) {
										return self.replaceValue ( this.value, currentTSElement.yAxisReplace );
									} else {  // just return the numerical value
										return this.value.toFixed(precision);
									}
								}
							},
							lineWidth: 3
						};
					// don't keep repeating the same yaxis label over and over for the same data type
					if ( k > 0) {
						standardYAxis.title['enabled'] = false;
						standardYAxis['lineWidth'] = 0;
					}
				
					// merge the standard y-axis with any specifics from the configuration file
					yAxis.push ({...standardYAxis, ...currentTSElement.yAxis });

					// data
					const tsData = [];
					this.chartUtilitiesService.convertTSToHighchartsFormat ( tsData, currentTSElement, k, markerEnabled );

					seriesArray.push (
					{
						data: tsData,
						yAxis: currentChartsWithData,
						name: seriesLabel,  // displays data type in the legend
						type: chartType, // defaults to line but we could use "area", etc.
						color: color,
						fillOpacity: 0.3,
						lineWidth: lineWidth,
						marker: {
							enabled: markerEnabled,
							symbol: markerSymbol  // symbol doesn't do anything if enabled is false
						},
						tooltip: {
							valueSuffix: ' ' + units, // station.properties["point_type_units_plural"]
							valueDecimals: tooltipPrecision,
							// elements can be accessed during chart formation below through
							// series.tooltipOptions.<element>
							tooltipReplace: currentTSElement.hasOwnProperty('tooltipReplace') ? 
								currentTSElement.tooltipReplace : null
						},
						pointRange: .5 * 3600 * 1000,
						precision: precision
						// sets column width to 30 minute data ... otherwise defaults to smallest time (5 minute data because of stage)
					});

					// alarms triggers
					if ( currentTSElement.hasOwnProperty('alarmTrigger') && currentTSElement.alarmTrigger != null ) {
						const alarmTrigger = currentTSElement.alarmTrigger;
						const alarmName = currentTSElement.alarmTriggerTitle;
						this.chartUtilitiesService.addLineToSeriesArray ( seriesArray, annotationArray, minDate, maxDate,
							alarmTrigger.limitValue, 'red', 'Alarm', alarmName, 'red', 0, 'top');
						
					}
					}

					currentChartsWithData++;
				}
				// console.log(yAxis);
			}
			// http://jsfiddle.net/VgCQV/
			// http://jsfiddle.net/gh/get/jquery/1.9.1/highslide-software/highcharts.com/tree/master/samples/highcharts/demo/synchronized-charts/
			// console.log(Highcharts.charts);

			if ( seriesArray.length > 0 ) {

			$('<div class="chart">')
				.appendTo('#chartContainer')
				.highcharts({
					chart: {
						marginLeft: 120, // Keep all charts left aligned
						zoomType: 'x',
						spacingTop: 20,
						spacingBottom: 20,
						height: 200,
						events: {
							load: this.chartUtilitiesService.addChartRemoveButton
						}
					},
					title: {
								text: '',
								align: 'left',
								margin: 0,
								x: 30
							},
					credits: {
								enabled: false
							},
					xAxis: {
								crosshair: true,
								events: {
									setExtremes: this.chartUtilitiesService.syncExtremes
								},
								lineColor: lineColor,
								tickLength: tickLength,
								lineWidth: 3,
								labels: {
									enabled: showXAxisLabels,
									align: 'center',
									format: xAxisFormat
								},
								offset: 10,
								softMin: minDate,
								softMax: maxDate,
								tickInterval: tickInterval,
								type: 'datetime'
							},
					yAxis:  yAxis,
					tooltip: {
								borderWidth: 0,
								headerFormat: '{point.x:%b %e %l:%M%p} ' + this.tzName + '<br>',  // see http://pubs.opengroup.org/onlinepubs/007908799/xsh/strftime.html
								style: {
									fontSize: '16px'
								},
								formatter: function() {
									let tt  = '<b>' + Highcharts.dateFormat('%y/%m/%e %l:%M%p', this.x) + '</b>';
									const actTime = this.x;
									this.series.chart.series.forEach(function(series, i) {
										// console.log(series);
										const diff = series.closestPointRange;
										let actPoint = series.points[series.points.length - 1];
										let color = series.color;
										series.data.forEach ( function(point, j) {
											if (point.x - actTime < diff ) {
												actPoint = point;
											}
										});
										if ( actPoint.y == null )
											actPoint = series.points[series.points.length - 2];

										let value = actPoint.y.toFixed(series.tooltipOptions.valueDecimals);
										if ( series.tooltipOptions.hasOwnProperty('tooltipReplace') && series.tooltipOptions.tooltipReplace != null ) {
											value = self.replaceValue ( value, series.tooltipOptions.tooltipReplace );
										}
										tt += '<br/>' + '<span style="color:' + color + '">' + value + '</span>';
									});
									/*  from elsewhere
									$(this.points[0].series.chart.series).each(function(i, series) {
										const diff = series.closestPointRange;
										let actPoint = null;
										$.each(series.data, function(i, point) {
											if (actTime - point.x < diff ) {
												actPoint = point;
											}
										});
										tt += '<br/>' + actPoint.y;
									});
									*/
									return tt;
								}
							},
					legend: {
						layout: 'horizontal',
						align: 'right',
						// x: 60,
						verticalAlign: 'top',
						y: -25,
						floating: true
					},
					annotations: annotationArray,
					annotationsOptions: { enabledButtons: false },
					plotOptions: {
						series: {  // general options for all series
							turboThreshold: 100000,  // maximum number of data points, default is 1,000
							states: { inactive: { opacity: 1}}, // added this because the annotation line kept going light
							stickyTracking: false,
							gapSize: gapSize,
							gapUnit: 'value'
						}
					},
					series: seriesArray
				});
			}

		}
		// this.bindMouseEvents();
	}

	// given a value, find it in the replacement array and return the replacement string
	replaceValue ( value, replacementArray ) {
		const replaceArray = replacementArray.filter (g => g['value'] == value);
		if ( replaceArray.length > 0 ) {
			return replaceArray[0].label;
		}
		return "";
	}

	// Calculate the number of days displayed in the charts
	numberOfDaysInChart (): number {
		if ( this.periodOfRecordSelected == '0' ) {	// custom dates
			return (this.endDate - this.startDate) / (24 * 60 * 60 * 1000);
		} else {
			return +this.periodOfRecordSelected;
		}
	}

	//
	// displayStationInformation displays information about the station in the
	// area below the page title
	//
	displayStationInformation (  ) {
		console.log('displayStationInformation');
		const id = +this.stationID;  // create a numerical value of stationID
		const dataLayer = this.dataLayerService.getDataLayerDetails('allStations');
		const stations = dataLayer.value.stationSummaries;
		const station_id = dataLayer.stationID;
		const novaScoresDictionary = dataLayer.value.novaScores;

		const station = stations.filter(dl => dl[station_id] === id)[0];
		// console.log(station);
		if ( station != null ) {
			const novaScores = this.chartUtilitiesService.getNovaScores(this.stationTSArray, station, novaScoresDictionary);
			this.stationNovaScores = novaScores;
			// console.log(this.stationNovaScores);

			for ( let i = 0; i < novaScores.length; i++) {
				this.stationNovaScores[i]['novaScoreClass'] = 'novaScore' + novaScores[i].novaScoreGroup;
			}

			this.currentStation.stationName = station[dataLayer.stationName];
			this.currentStation.stationId = station[station_id];
			this.currentStation.stationElevation = station.elevation;
			this.currentStation.longitude = station.longitude;
			this.currentStation.latitude = station.latitude;

			this.displayMap();
			this.displayImages();
		}
	}


	//
	// clearGraphs removes the series from CombinedChart
	//
	clearGraphs() {
		for ( let chartIndex = 0; chartIndex < Highcharts.charts.length; chartIndex++ ) {
			// console.log( 'Number of Highchart charts: ' + Highcharts.charts.length);
			// console.log( Highcharts.charts);
			// console.log('Destroying a chart');
			let c = Highcharts.charts[chartIndex];
			// console.log(c);
			if ( c != undefined ) {
				c.destroy();
			}
		}
		Highcharts.charts.length = 0;

		const div = document.getElementById('chartContainer');
		if ( div == null ) {
			return;
		}

		// remove all existing charts
		while (div.hasChildNodes()) {
			div.removeChild(div.lastChild);
		}
	}

	//
	// newStationSelected is called when a new station
	// is selected from the pulldown list and when the
	// dashboard template is first selected.
	//
	newStationSelected (newStation) {
		// console.log("newStationSelected");
		console.log(newStation);
		if ( this.preview === false ) {
			const tree = this._router.parseUrl(this.location.path());
			tree.queryParams['id'] = this.templateID;
			console.log(tree);
			const link = ['/stationDashboard/' + newStation ];
			this._router.navigate(link, { queryParams: tree.queryParams});
			// this.location.replaceState('/dashboardTemplate/' + newStation + '?id=' + this.templateID );
		}
		this.stationID = newStation;
		this.listSelection = +this.stationID;
		this.clearGraphs();
		this.displayGraphsForStationID();
		this.displayStationInformation ();
	}

	newPeriodOfRecordSelected (newPOR) {
		// verify the dates are valid if a custom date range has been requested
		if ( newPOR === '0' && this.startDate > this.endDate) {
			alert('Start date is after end date.  Please select a different start or end date.')
			return;
		}
		this.periodOfRecordSelected = newPOR;
		this.clearGraphs();
		this.displayGraphsForStationID();
	}


	// given an array of novaScoreobjects from getNovaScores, return the highest
	getMaxNovaScore ( novaScores ) {
		let maxNovaScore = 1;
		for ( let i = 0; i < novaScores.length; i++) {
			maxNovaScore = Math.max(maxNovaScore, novaScores[i].novaScoreGroup);
		}
		return maxNovaScore;
	}

	// given a novaScore group number, return the corresponding color
	// these match the color use on the map
	getNovaScoreClass(maxNovaScore) {
		let novaScoreClass = '';
		switch (maxNovaScore) {
			case 5: novaScoreClass = 'novaScore5'; break;
			case 4: novaScoreClass = 'novaScore4'; break;
			case 3: novaScoreClass = 'novaScore3'; break;
			case 2: novaScoreClass = 'novaScore2'; break;
			default: novaScoreClass = 'novaScore1'; break;
		}
		return novaScoreClass;
	}


	// this populates the station selection pulldown
	populateStationSelection() {

		this.dataLayerService.parseAllStations().then(allStations => {
			// console.log(allStations);
			const stations = allStations['stationSummaries'];

			const dataLayer = this.dataLayerService.getDataLayerDetails('allStations');
			// console.log(dataLayer.value);  this is the same thing as allStations above
			// set up a listener - refresh table every time the data is refreshed
			/* commenting out for now.  causes problems when users are zoomed into data and a refresh occurs.
			dataLayer.valueObservable.subscribe ( data => {
				console.log('Refreshing table');
				this.clearGraphs();
				this.displayGraphsForStationID();
			})
			*/

			const station_id = dataLayer.stationID;
			const station_name = dataLayer.stationName;
			this.station_name = station_name;  // save this for later when subsequent filtering is necessary
			const novaScoresDictionary = dataLayer.value.novaScores;

			// tslint:disable-next-line:forin
			for ( const i in stations ) {

				let newStation: any; // StationData;
				const novaScores = this.chartUtilitiesService.getNovaScores(this.stationTSArray, stations[i], novaScoresDictionary);
				// console.log(novaScores);
				const maxNovaScore = this.getMaxNovaScore(novaScores);
				const novaScoreClass = this.getNovaScoreClass(maxNovaScore);
				// console.log( stations[i][station_name] + ": " + maxNovaScore + ": " + novaScoreClass );
				newStation = {
						id: stations[i][station_id],
						name: stations[i][station_name],
						label: stations[i][station_id] + ' - ' + stations[i][station_name] + ' (' + maxNovaScore + ')',
						value: stations[i][station_id],
						novaScore: maxNovaScore,
						novaScoreClass:  novaScoreClass};

				this.binaryInsertByNovaScore( newStation, this.originalStationList, undefined, undefined );
			}

			console.log('Number of stations before/after sort ' + stations.length + '/' + this.originalStationList.length );

			// if the user arrived here by clicking on "Station Dashboard" rather than by selecting an individual station,
			// a reasonable default must be selected.
			this.filterStationList ( );
			if ( this.stationID === '-999' && this.originalStationList.length > 0) {
				this.setDefaultStation();
			} else {
				this.listSelection = +this.stationID;
			}
			
			// displays the first station selected.  
			this.displayStationInformation ();

			// emit the stationList in case there are any components listening
			this.onGetStationList();
		});
	}

	// setDefaultStation sets the dashboard to display the first station in the station selection
	// list if no previous stations have been viewed.
	setDefaultStation() {
		// console.log("setDefaultStation");
		if ( this.stationList.length > 0 ) {
			this.stationID = this.stationList[0].id;
			this.newStationSelected(this.stationID);
		}
	}

	// utility function for populateStationSelection
	// orders list by id only
	binaryInsert(newStation, array, startVal, endVal) {
		const length = array.length;
		const start = typeof(startVal) !== 'undefined' ? startVal : 0;
		const end = typeof(endVal) !== 'undefined' ? endVal : length - 1; // !! endVal could be 0 don't use || syntax
		const m = start + Math.floor((end - start) / 2);

		if (length === 0) {
			array.push(newStation);
			return;
		}

		if (newStation.id > array[end].id) {
			array.splice(end + 1, 0, newStation);
			return;
		}

		if (newStation.id < array[start].id) {
			array.splice(start, 0, newStation);
			return;
		}

		if (start >= end) {
			return;
		}

		if (newStation.id < array[m].id) {
			this.binaryInsert(newStation, array, start, m - 1);
			return;
		}

		if (newStation.id > array[m].id) {
			this.binaryInsert(newStation, array, m + 1, end);
			return;
		}

		// don't insert duplicates
	}

	// utility function for populateStationSelection
	// orders list by id and novaScore
	binaryInsertByNovaScore(newStation, array, startVal, endVal) {
		const length = array.length;
		const start = typeof(startVal) !== 'undefined' ? startVal : 0;
		const end = typeof(endVal) !== 'undefined' ? endVal : length - 1; // !! endVal could be 0 don't use || syntax
		const m = start + Math.floor((end - start) / 2);

		if (length === 0) {
			array.push(newStation);
			return;
		}

		if (newStation.novaScore < array[end].novaScore || (newStation.novaScore === array[end].novaScore && newStation.id > array[end].id)) {
			array.splice(end + 1, 0, newStation);
			return;
		}

		if (newStation.novaScore > array[start].novaScore ||
			(newStation.novaScore === array[start].novaScore &&
				newStation.id < array[start].id)) {
			array.splice(start, 0, newStation);
			return;
		}

		if (start >= end) {
			return;
		}

		if (newStation.novaScore > array[m].novaScore || (newStation.novaScore === array[m].novaScore && newStation.id < array[m].id)) {
			this.binaryInsertByNovaScore(newStation, array, start, m - 1);
			return;
		} else {
			this.binaryInsertByNovaScore(newStation, array, m + 1, end);
			return;
		}

		// don't insert duplicates
	}

}
